/**
 * displayPersonName: the one casing rule for a person's name, applied at
 * RENDER, never at write.
 *
 * ## The row
 *
 * TYP-02, from the 22 August walkthrough: the same person renders SHOUTING on
 * one screen and in ordinary mixed case on another. The sidebar frame from
 * 25 August reads "EMMANUEL PA..." because the value stored for that person came
 * off an identity document, and identity documents are printed in capitals. The
 * ask was one casing rule at render time, so that fixing it never depends on
 * cleaning up what is already in the database.
 *
 * ## What it does, and the much larger thing it deliberately does not do
 *
 * It only acts on a name that carries NO case information of its own: a string
 * that is entirely upper case, or entirely lower case. Those two are the ones a
 * machine produced. Anything with a mix of both was cased by a human being who
 * typed it, and is returned with its letters exactly as typed (whitespace is
 * normalised on every input, see below):
 *
 *   "EMMANUEL PAUL PRINSLOO"  -> "Emmanuel Paul Prinsloo"
 *   "thandi nkosi"            -> "Thandi Nkosi"
 *   "Pieter van der Merwe"    -> unchanged, the particle survives
 *   "McDonald"                -> unchanged
 *   "DE WET-NKOSI"            -> "De Wet-Nkosi"
 *   "de Wet-Nkosi"            -> unchanged: it is mixed case, so a person typed
 *                                it, opening particle and all
 *
 * This is a display transform, not a correction. It never writes, and the full
 * stored string stays available for the `title` tooltip and for anything legal.
 *
 * ## The judgement calls, written down
 *
 * - **Afrikaans and Dutch particles** (van, van der, du, de, le) are
 *   capitalised when no first name precedes them and lower case after one
 *   (QA 4 Oct, P2-18): "Van der Merwe" and "Mr Van der Merwe", but "Johan van
 *   der Merwe" and "J van der Merwe". That is how they are set in South African
 *   practice, and getting it wrong is the single most visible way to misspell a
 *   local surname. Only the first word of a run is capitalised. A title (Mr,
 *   Mrs, Ms, Miss, Dr, Prof, Adv, Mnr, Mev, Me, Mej, Ds) is not a first name.
 * - **Accented letters are letters** (P2-18): "ÉTIENNE" is "Étienne", not
 *   "éTienne".
 * - **Mc and Mac are NOT special-cased.** "MACDONALD" could be Macdonald or
 *   MacDonald and there is no way to tell from the string. Guessing which is the
 *   kind of invention this product refuses elsewhere, so an all-caps Mac name
 *   comes out as "Macdonald" and a human can correct it. Documented rather than
 *   silently guessed.
 * - **Initials survive.** A single letter stays a capital ("J P Nkosi"), and so
 *   does a run of dotted initials ("J.P. du Toit", "T.K.", the "N.O." nominee
 *   marker) and a generation numeral II to XII ("John Smith III").
 * - **There is NO short-word acronym rule for a person** (round 4, 22 Sep).
 *   An earlier version kept a two-letter consonant-only token upper case ("JP")
 *   as an initial pair. For a person that guess is wrong more often than right
 *   and it is the same family of rule (the W5 backend recaser, which kept any
 *   all-caps word of three letters or fewer) that turned "ANN SMITH" into "ANN
 *   Smith". "JP NKOSI" is now "Jp Nkosi"; a person who
 *   wants "JP" types it that way, and mixed case is left alone.
 * - **Hyphens and apostrophes case their following letter** ("Jean-Pierre",
 *   "O'Brien"), because those are always word boundaries in a name, except a
 *   possessive s ("Founder's", QA 4 Oct).
 * - **Whitespace runs collapse to one space and the ends are trimmed**, on
 *   every input, mixed case and email addresses included; blank gives ''.
 *   (Round 4 add-on: this used to preserve surrounding whitespace, and the
 *   backend's twin normalises it. The two must return identical output.)
 * - **An email address is not a name** and is returned as given (after the
 *   whitespace step): the shells and several senders fall back to one.
 *
 * ## The contract with the backend
 *
 * Round 4 (22 Sep): the backend implements this same rule for the text it writes
 * (`backend/src/lib/format/person-name.ts`). The functions below are a
 * line-for-line port of that file so every input returns identical output. Since
 * 4 Oct both are held to ONE vector table, `shared/name-vectors/person-name.json`
 * (this side's test: `__tests__/person-name-vectors.test.ts`), and
 * `scripts/check-name-parity.ts` runs every row and a generated corpus
 * through BOTH functions and fails on any difference. Change the rule only by
 * changing the table first, then both functions together.
 *
 * ## Where it runs
 *
 * Every screen that renders a person's name, routed in round 4 (T10 report,
 * "Round 4"). It does NOT run where the exact stored value is the point: a
 * type-to-confirm prompt, an edit field's starting value, a request body, an
 * export file. Those sites are listed in the same report.
 */

/** Particles: capitalised when no first name precedes them, lower case after
 *  one. Exactly the briefed list (round 4): van, van der, van den, van de, de,
 *  der, den, du, le, la, von, ter, ten, dos, da, di. The compounds are runs of
 *  these, each word judged on its own. Same set as the backend's `PARTICLES`. */
const PARTICLES: ReadonlySet<string> = new Set([
  'van', 'de', 'der', 'den', 'du', 'le', 'la', 'von', 'ter', 'ten', 'dos', 'da', 'di',
]);

/** Titles: one capital, and not a first name for the particle rule. Same set
 *  as the backend's `TITLES`. */
const TITLES: ReadonlySet<string> = new Set([
  'mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'adv', 'mnr', 'mev', 'me', 'mej', 'ds',
]);

/** A token of initials: one letter, or letters each followed by a full stop
 *  ("J", "J.", "J.P.", "j.p", "N.O."). Stays (or becomes) upper case. Same
 *  pattern as the backend's `INITIALS`. */
const INITIALS = /^(?:\p{L}\.)*\p{L}\.?$/u;

/** A generation numeral. A bare "I" is an initial and is caught above. */
const ROMAN_NUMERAL = /^(?:II|III|IV|VI|VII|VIII|IX|XI|XII)$/i;

/** Upper-case, except a letter whose capital is not one letter (the German
 *  sharp s) is left as it is, so the rule stays idempotent. */
function upperCase(s: string): string {
  return Array.from(s, (c) => {
    const u = c.toUpperCase();
    return u.length === c.length ? u : c;
  }).join('');
}

/** Upper-case the first LETTER of a run and lower-case the rest of it, so a
 *  run that opens with punctuation still gets its capital. */
function capitaliseRun(run: string): string {
  const i = run.search(/\p{L}/u);
  if (i === -1) return run;
  return run.slice(0, i) + upperCase(run.charAt(i)) + run.slice(i + 1).toLowerCase();
}

/** Case one whitespace-delimited token of a one-case name. `firstNameSeen` is
 *  false while only titles have come before it. Deliberately NO short-word
 *  acronym rule (see the header). */
function caseToken(token: string, firstNameSeen: boolean): string {
  if (INITIALS.test(token)) return upperCase(token);
  if (ROMAN_NUMERAL.test(token)) return upperCase(token);
  const lower = token.toLowerCase();
  if (PARTICLES.has(lower)) return firstNameSeen ? lower : capitaliseRun(lower);
  // A hyphen or an apostrophe (straight or curly) starts a new capital, except a
  // possessive s at the end of the token ("Founder's").
  const parts = lower.split(/([-'\u2019])/);
  return parts
    .map((part, i) => {
      if (i % 2 === 1) return part;
      const possessive = i === parts.length - 1 && i > 0 && part === 's' && parts[i - 1] !== '-';
      return possessive ? part : capitaliseRun(part);
    })
    .join('');
}

function isTitle(token: string): boolean {
  return TITLES.has(token.toLowerCase().replace(/\.$/, ''));
}

/**
 * Render a person's name in the one casing rule (see the header). Null,
 * undefined, a non-string and blank give ''. Never throws. Idempotent: its own
 * output is mixed case and comes back unchanged.
 */
export function displayPersonName(name: string | null | undefined): string {
  if (typeof name !== 'string') return '';
  const normalised = name.replace(/\s+/g, ' ').trim();
  if (!normalised) return '';
  if (normalised.includes('@')) return normalised;
  const hasUpper = /\p{Lu}/u.test(normalised);
  const hasLower = /\p{Ll}/u.test(normalised);
  // Both cases present: a person typed it that way. No cased letter at all:
  // there is nothing this rule can say about it.
  if (hasUpper === hasLower) return normalised;
  let firstNameSeen = false;
  return normalised
    .split(' ')
    .map((token) => {
      const out = caseToken(token, firstNameSeen);
      if (!isTitle(token)) firstNameSeen = true;
      return out;
    })
    .join(' ');
}

/**
 * THE REORDER STEP, separate from casing and run before it.
 *
 * An SA identity card typesets the name "SURNAME, FORENAMES" ("SMITH-JONES,
 * MARY"); everywhere else a person reads forenames first. This only moves a
 * value in that shape, and changes no letter's case: `displayPersonName` does
 * that afterwards. Anything without the comma, and a blank, comes back as it
 * went in. `identityDisplayName` in the structure builder is this function.
 */
export function reorderSurnameFirst(raw: string): string {
  const t = raw.trim();
  if (!t) return raw;
  const m = /^([^,]+),\s*(.+)$/.exec(t);
  if (!m) return raw;
  const surname = m[1]!.trim();
  const forenames = m[2]!.trim();
  return forenames && surname ? `${forenames} ${surname}` : raw;
}
